import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Stack } from '@mui/material';
import { FaUserEdit } from 'react-icons/fa';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useNavigate } from 'react-router-dom';
import SonDuyurular from './dashboard/SonDuyurular';
import Haberler from './dashboard/Haberler';
import BildirimYayinla from './dashboard/BildirimYayinla';
import Bültenler from './dashboard/Bültenler';
import Etkinlikler from './dashboard/Etkinlikler';
import SonProjeler from './dashboard/SonProjeler';
import ÜyelerKartı from './dashboard/ÜyelerKartı';
import OcakbaşıGruplarıKartı from './dashboard/OcakbaşıGruplarıKartı';
import YaklaşanEtkinlikKartı from './dashboard/YaklaşanEtkinlikKartı';
import CreateAnnouncementDialog from '../components/CreateAnnouncementDialog';
import { fetchAllUpdateRequests } from '../controllers/memberManagementController';
import { fetchParticipationCancellationSummary } from '../controllers/participationCancellationController';

const Dashboard = () => {
  const [createOpen, setCreateOpen] = useState(false);
  const refreshKeyRef = useRef(0);
  const navigate = useNavigate();
  const [pendingUpdateCount, setPendingUpdateCount] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingCancellationCount, setPendingCancellationCount] = useState(null);
  const [pendingCancellationLoading, setPendingCancellationLoading] = useState(false);

  const handleCreateSuccess = () => {
    // Trigger refresh of SonDuyurular component
    refreshKeyRef.current += 1;
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setPendingLoading(true);
      try {
        const { data, meta } = await fetchAllUpdateRequests({ pageNumber: 1, pageSize: 1, status: 0 });
        if (!active) return;
        const total =
          meta?.totalCount ??
          meta?.totalItems ??
          (typeof meta?.totalPages === 'number' ? meta.totalPages * 1 : undefined) ??
          (Array.isArray(data) ? data.length : 0);
        setPendingUpdateCount(total || 0);
      } catch (e) {
        if (!active) return;
        setPendingUpdateCount(0);
      } finally {
        if (active) setPendingLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setPendingCancellationLoading(true);
      try {
        const data = await fetchParticipationCancellationSummary();
        if (!active) return;
        setPendingCancellationCount(data?.pendingCount ?? 0);
      } catch {
        if (!active) return;
        setPendingCancellationCount(null);
      } finally {
        if (active) setPendingCancellationLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleGoToPendingUpdates = () => {
    navigate('/members', { state: { openUpdateTab: true, initialUpdateStatus: 0 } });
  };

  const handleGoToPendingCancellations = () => {
    navigate('/events/cancellation-requests?status=0');
  };

  return (
    <Box>
      <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444', mb: 2 }}>
        Dashboard
      </Typography>

      {/* İstatistik Kartları ve Etkinlikler */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
        {/* Sol Taraf - 2x2 Grid (4 Kart) */}
        <Box sx={{ flex: '0 0 calc(33.333% - 5.33px)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* İlk Satır */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: '0 0 calc(50% - 8px)', minWidth: 0 }}>
              <ÜyelerKartı />
            </Box>
            <Box sx={{ flex: '0 0 calc(50% - 8px)', minWidth: 0 }}>
              <OcakbaşıGruplarıKartı />
            </Box>
          </Box>
          {/* İkinci Satır */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: '0 0 calc(50% - 8px)', minWidth: 0 }}>
              <YaklaşanEtkinlikKartı />
            </Box>
            <Box sx={{ flex: '0 0 calc(50% - 8px)', minWidth: 0 }}>
              <Card
                sx={{
                  border: '1px solid #f1f1f1',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
                  },
                }}
                onClick={handleGoToPendingUpdates}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                      }}
                    >
                      <FaUserEdit style={{ fontSize: '20px', color: '#0E4D94' }} />
                    </Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#666666', mb: 0.5 }}>
                      Üye Güncelleme Talepleri
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444', lineHeight: 1.3 }}>
                      {pendingLoading
                        ? 'Yükleniyor...'
                        : pendingUpdateCount && pendingUpdateCount > 0
                        ? `${pendingUpdateCount} beklemede`
                        : 'Beklemede talep yok'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>
          <Card
            sx={{
              border: '1px solid #f1f1f1',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              cursor: pendingCancellationCount != null ? 'pointer' : 'default',
              '&:hover': pendingCancellationCount != null ? {
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
              } : {},
            }}
            onClick={pendingCancellationCount != null ? handleGoToPendingCancellations : undefined}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <EventBusyIcon sx={{ fontSize: 22, color: '#0E4D94' }} />
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#666666', mb: 0.5 }}>
                  Katılım İptal Talepleri
                </Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#444444', lineHeight: 1.3 }}>
                  {pendingCancellationLoading
                    ? 'Yükleniyor...'
                    : pendingCancellationCount == null
                    ? '—'
                    : pendingCancellationCount > 0
                    ? `${pendingCancellationCount} beklemede`
                    : 'Bekleyen talep yok'}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
          <BildirimYayinla />
        </Box>

        {/* Sağ Taraf - Etkinlikler (Tam Yükseklik) */}
        <Box sx={{ flex: '0 0 calc(66.666% - 10.67px)', minWidth: 0 }}>
          <Etkinlikler />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mt: 3 }}>
        <SonDuyurular key={refreshKeyRef.current} />
        <SonProjeler />
        <Haberler />
        <Bültenler />
      </Box>

      <CreateAnnouncementDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Box>
  );
};

export default Dashboard;